import { ControlButton } from "@ilabel/ui";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { Outlet, useLocation, useNavigate, useNavigation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { routeContracts } from "@ilabel/contracts";
import {
  appSessionQueryPolicy,
  isProductMenuEnabled,
  createAuthorizationScopeKey,
  useProductConfig,
} from "@ilabel/platform-core";
import { InitialPasswordReminder } from "@ilabel/profile";
import {
  requestProtectedAction,
  runForcedEditExit,
  Breadcrumbs,
  createBreadcrumbEntityCache,
  ConfirmDialog,
  MenuItem as ActionMenuItem,
  NavigationIcon,
  NoticeDialog,
  Toast,
  useAsyncAction,
  useAutomaticBreadcrumbs,
  type BreadcrumbEntityResolver,
  type BreadcrumbRouteRule,
  type NavigationIconName,
} from "@ilabel/ui";

import {
  api,
  ApiError,
  AUTH_EVENT_STORAGE_KEY,
  type AuthEvent,
} from "../lib/api-client.js";
import {
  APP_SESSION_QUERY_KEY,
  type AppSessionContextValue,
  type AppSessionData,
} from "./app-session-context.js";
import { SvgIcon } from "./icons.js";
import { createPageResourceClient } from "./page-resource-client.js";
import { SessionQueryBoundary } from "./session-query-boundary.js";
import { forceEditNavigation } from "./edit-protection-navigation.js";
import { AppPageLoading } from "./app-page-loading.js";
import { preloadVlaRoute } from "./route-loaders.js";
import { ErrorBoundary } from "react-error-boundary";
import { AppInitializationError, AppInitializationFailure, AppRouteError } from "./app-route-error.js";

interface MenuChild {
  readonly key: string;
  readonly label: string;
  readonly path: string;
}
interface MenuItem {
  readonly key: string;
  readonly label: string;
  readonly icon: NavigationIconName;
  readonly path?: string;
  readonly children?: readonly MenuChild[];
}
class BrowserAccountSwitchError extends Error {}
interface MenuRulesData {
  readonly visibleMenus?: readonly {
    readonly key?: string;
    readonly visible?: boolean;
  }[];
  readonly defaultRoute?: string;
}
interface BreadcrumbProject {
  readonly id: number;
  readonly projectName: string;
}
interface BreadcrumbConfigLibrary {
  readonly id: number;
  readonly name: string;
}
const menu: readonly MenuItem[] = [
  {
    key: "workbench",
    label: "工作台",
    icon: "dashboard",
    path: "/workbench",
  },
  {
    key: "project-management",
    label: "项目管理",
    icon: "project",
    path: "/project-management",
  },
  {
    key: "annotation-configuration",
    label: "配置管理",
    icon: "configuration",
    children: [
      {
        key: "config-management",
        label: "动作标签库",
        path: "/config-management",
      },
      {
        key: "object-management",
        label: "操作对象库",
        path: "/config-management?view=object",
      },
    ],
  },
  {
    key: "team-members",
    label: "团队与成员",
    icon: "team",
    path: "/team-members",
  },
] as const;

const vlaBreadcrumbRoutes: readonly BreadcrumbRouteRule[] = [
  {
    path: "/dataset-management",
    requiredQuery: ["projectId"],
    items: [
      { label: "项目管理", path: "/project-management" },
      { entity: "project", idParam: "projectId", labelField: "projectName", fallbackLabel: "项目" },
    ],
  },
  {
    path: "/annotation-data-management",
    requiredQuery: ["projectId", "datasetId"],
    items: [
      { label: "项目管理", path: "/project-management" },
      {
        entity: "project",
        idParam: "projectId",
        labelField: "projectName",
        fallbackLabel: "项目",
        path: "/dataset-management?projectId=:projectId",
      },
      { entity: "dataset", idParam: "datasetId", scopeParams: ["projectId"], labelField: "datasetName", fallbackLabel: "数据集" },
    ],
  },
  {
    path: "/label-library-detail",
    items: [
      { label: "配置管理" },
      { label: "动作标签库", path: "/config-management" },
      {
        entity: "config-library",
        idParam: "libraryId",
        labelField: "name",
        fallbackLabel: "标签库",
      },
    ],
  },
  {
    path: "/config-management",
    query: { view: "object" },
    requiredQuery: ["libraryId"],
    items: [
      { label: "配置管理" },
      {
        label: "操作对象库",
        path: "/config-management?view=object",
      },
      {
        entity: "config-library",
        idParam: "libraryId",
        labelField: "name",
        fallbackLabel: "对象库",
      },
    ],
  },
  {
    path: "/profile-settings",
    items: [{ label: "资料设置" }],
  },
] as const;

function activateWithKeyboard(
  event: KeyboardEvent<HTMLElement>,
  action: () => void,
) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  action();
}

function UserMenu({
  anchor,
  onClose,
  onProfile,
  onLogout,
}: {
  readonly anchor: HTMLButtonElement;
  readonly onClose: () => void;
  readonly onProfile: () => void;
  readonly onLogout: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const rect = anchor.getBoundingClientRect();
  useEffect(() => {
    const dismiss = (event: MouseEvent) => {
      if (
        anchor.contains(event.target as Node) ||
        menuRef.current?.contains(event.target as Node)
      )
        return;
      onClose();
    };
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [anchor, onClose]);
  return createPortal(
    <div
      className="popup-menu shell-user-menu popup-show"
      role="menu"
      ref={menuRef}
      style={{
        position: "fixed",
        right: Math.max(8, window.innerWidth - rect.right),
        top: rect.bottom + 8,
      }}
      onMouseLeave={onClose}
    >
      <ActionMenuItem
        data-user-action="profile"
        onClick={onProfile}
      >
        <span>资料设置</span>
      </ActionMenuItem>
      <ActionMenuItem
        tone="danger"
        data-user-action="logout"
        onClick={onLogout}
      >
        <span>退出系统</span>
      </ActionMenuItem>
    </div>,
    document.body,
  );
}

export function AppShell() {
  const navigation = useNavigation();
  const productConfig = useProductConfig();
  const [collapsed, setCollapsed] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [userName, setUserName] = useState("秦杰");
  const [userMenuAnchor, setUserMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const { busy: logoutBusy, acquire: beginLogout, release: finishLogout } = useAsyncAction();
  const [logoutFeedback, setLogoutFeedback] = useState("");
  const [sessionInterrupted, setSessionInterrupted] = useState("");
  const [visibleMenuKeys, setVisibleMenuKeys] = useState<Set<string> | null>(
    null,
  );
  const [menuScope, setMenuScope] = useState("");
  const [menuError, setMenuError] = useState("");
  const [menuRetry, setMenuRetry] = useState(0);
  const expectedAccountId = useRef("");
  const configuredMenu = useMemo(
    () =>
      menu.filter((entry) => isProductMenuEnabled(productConfig, entry.key)),
    [productConfig],
  );
  const queryClient = useQueryClient();
  const localSession = useSyncExternalStore(api.sessionBoundary.subscribe, api.sessionBoundary.getSnapshot, api.sessionBoundary.getSnapshot);
  const [mountedIdentityRevision] = useState(localSession.revision);
  const locallySignedOut = localSession.status !== "active" || localSession.revision !== mountedIdentityRevision;
  const interruptSession = useCallback(
    (message: string) => {
      setSessionInterrupted(message);
      void queryClient.cancelQueries();
    },
    [queryClient],
  );
  const sessionQuery = useQuery({
    queryKey: APP_SESSION_QUERY_KEY,
    queryFn: async () => {
      const nextSession = await api<AppSessionData>("/session/context");
      if (!String(nextSession?.account?.id ?? "").trim()) throw new AppInitializationError("初始化信息未就绪");
      const accountId = String(nextSession.account?.id ?? "");
      if (
        expectedAccountId.current &&
        accountId &&
        accountId !== expectedAccountId.current
      )
        throw new BrowserAccountSwitchError(
          "当前浏览器已切换登录账号",
        );
      expectedAccountId.current = accountId;
      return nextSession;
    },
    enabled: !sessionInterrupted && !locallySignedOut,
    staleTime: appSessionQueryPolicy.bootstrapFreshnessMs,
    gcTime: 0,
    retry: false,
    refetchInterval: appSessionQueryPolicy.refreshIntervalMs,
    refetchOnWindowFocus: true,
  });
  const sessionRejected = sessionQuery.error instanceof BrowserAccountSwitchError ||
    (sessionQuery.error instanceof ApiError && [401, 403].includes(sessionQuery.error.status));
  const session = sessionInterrupted || sessionRejected || locallySignedOut ? null : (sessionQuery.data ?? null);
  const authorizationScope = createAuthorizationScopeKey(session);
  const resources = useMemo(() => createPageResourceClient(queryClient, authorizationScope), [queryClient, authorizationScope]);
  useEffect(() => () => resources.dispose(), [resources]);
  const breadcrumbEntities = useMemo(() => createBreadcrumbEntityCache(), [authorizationScope]);
  const refreshSessionQuery = sessionQuery.refetch;
  const revalidateSession = useCallback(async () => {
    const result = await refreshSessionQuery();
    return result.isError ? null : (result.data ?? null);
  }, [refreshSessionQuery]);
  const sessionContext = useMemo<AppSessionContextValue>(
    () => ({
      breadcrumbEntities,
      resources,
      session,
      sessionPending: sessionQuery.isPending,
      sessionError: sessionQuery.data ? null : sessionQuery.error,
      revalidateSession,
    }),
    [
      breadcrumbEntities,
      resources,
      revalidateSession,
      session,
      sessionQuery.data,
      sessionQuery.error,
      sessionQuery.isPending,
    ],
  );
  const location = useLocation();
  const navigate = useNavigate();
  const breadcrumbAccountId = String(session?.account?.id ?? "");
  const resolveBreadcrumbEntity = useCallback<BreadcrumbEntityResolver>(
    async ({ entity, id, params }) => {
      const numericId = Number(id);
      if (!Number.isSafeInteger(numericId) || numericId <= 0) return undefined;
      if (entity === "project") {
        const projects = await resources.read<readonly BreadcrumbProject[]>("/projects");
        return projects.find((item) => item.id === numericId);
      }
      if (entity === "dataset") {
        const projectId = Number(params.get("projectId"));
        if (!Number.isSafeInteger(projectId) || projectId <= 0) return undefined;
        const datasets = await resources.read<readonly { id: number; datasetName: string }[]>(`/datasets?projectId=${projectId}`);
        return datasets.find((item) => item.id === numericId);
      }
      if (entity === "config-library") {
        const libraries = await resources.read<readonly BreadcrumbConfigLibrary[]>("/config-libraries");
        return libraries.find((item) => item.id === numericId);
      }
      return undefined;
    },
    [resources],
  );
  const route = useMemo(
    () =>
      routeContracts.find((candidate) => location.pathname === candidate.path),
    [location.pathname],
  );
  const breadcrumbFallback = useMemo(
    () => [{ label: route?.title ?? "工作台" }],
    [route?.title],
  );
  const requiredMenu = ["/annotation-data-management", "/dataset-management"].includes(location.pathname)
    ? "project-management"
    : location.pathname === "/label-library-detail" || location.pathname === "/config-management"
      ? "annotation-configuration"
      : configuredMenu.find((item) => item.path === location.pathname)?.key;
  const routeAuthorized = !requiredMenu || visibleMenuKeys?.has(requiredMenu);
  const breadcrumbs = useAutomaticBreadcrumbs({
    navigation: menu,
    routes: vlaBreadcrumbRoutes,
    pathname: location.pathname,
    search: location.search,
    resolveEntity: resolveBreadcrumbEntity,
    fallbackItems: breadcrumbFallback,
    enabled: Boolean(breadcrumbAccountId && routeAuthorized),
    cacheKey: authorizationScope,
    entityCache: breadcrumbEntities,
  });
  const breadcrumbBackPath = breadcrumbs[0]?.path;
  const activeSubMenu =
    location.pathname === "/config-management" &&
    new URLSearchParams(location.search).get("view") === "object"
      ? "object-management"
      : route?.activeSubMenu;

  useEffect(() => {
    if (!session || sessionInterrupted) return undefined;
    let live = true;
    setMenuError("");
    void api<MenuRulesData>("/rules/menus")
      .then((rules) => {
        if (!live) return;
        const visible = new Set(
          (rules.visibleMenus ?? [])
            .filter((item) => item.visible !== false)
            .map((item) => String(item.key ?? ""))
            .filter(Boolean),
        );
        setVisibleMenuKeys(visible);
        setMenuScope(authorizationScope);
        const required =
          ["/annotation-data-management", "/dataset-management"].includes(location.pathname)
            ? "project-management"
            : location.pathname === "/label-library-detail" ||
                location.pathname === "/config-management"
              ? "annotation-configuration"
              : configuredMenu.find((item) => item.path === location.pathname)
                  ?.key;
        if (required && !visible.has(required))
          forceEditNavigation(rules.defaultRoute || "/workbench", () => navigate(rules.defaultRoute || "/workbench", { replace: true }));
      })
      .catch((error: unknown) => {
        if (live && error instanceof ApiError && error.status === 401)
          interruptSession(error.message);
        else if (live) setMenuError(error instanceof Error ? error.message : "页面权限加载失败");
      });
    return () => {
      live = false;
    };
  }, [
    interruptSession,
    configuredMenu,
    location.pathname,
    navigate,
    sessionInterrupted,
    authorizationScope,
    menuRetry,
  ]);

  useEffect(() => {
    const error = sessionQuery.error;
    if (error instanceof BrowserAccountSwitchError)
      interruptSession(error.message);
    else if (error instanceof ApiError && error.status === 401)
      interruptSession(error.message);
  }, [interruptSession, sessionQuery.error, sessionQuery.errorUpdatedAt]);

  useEffect(() => {
    if (!session) return;
    setUserName(session.account?.name || session.account?.username || "秦杰");
  }, [session]);

  useEffect(() => {
    const receiveAuthEvent = (event: StorageEvent) => {
      if (event.key !== AUTH_EVENT_STORAGE_KEY || !event.newValue) return;
      try {
        const authEvent = JSON.parse(event.newValue) as AuthEvent;
        if (
          authEvent.kind === "browser-account-switched" ||
          (expectedAccountId.current &&
            authEvent.accountId !== expectedAccountId.current)
        )
          interruptSession("当前浏览器已切换登录账号");
        else if (
          authEvent.kind === "session-replaced" &&
          authEvent.accountId === expectedAccountId.current
        )
          interruptSession("当前账号已在其他设备或浏览器登录");
      } catch {
        // Ignore malformed cross-tab events and keep polling the server.
      }
    };
    window.addEventListener("storage", receiveAuthEvent);
    return () => window.removeEventListener("storage", receiveAuthEvent);
  }, [interruptSession]);

  const go = (path: string) => {
    setFlyoutOpen(false);
    void preloadVlaRoute(path).catch(() => undefined);
    navigate(path);
  };
  const confirmLogout = async () => {
    if (!beginLogout()) return false;
    setLogoutFeedback("");
    try {
      await api.logout();
      return true;
    } catch {
      // The local boundary already stopped this identity; the login page
      // distinguishes confirmed logout from an unconfirmed server outcome.
      return false;
    } finally {
      finishLogout();
    }
  };

  useEffect(() => {
    if (!locallySignedOut) return;
    void queryClient.cancelQueries();
    queryClient.clear();
    void forceEditNavigation("/login", () => navigate("/login", {
      replace: true,
      state: localSession.status === "active" ? { notice: "登录身份已更新，请重新登录后继续。" } : null,
    }));
  }, [locallySignedOut, localSession.status, navigate, queryClient]);

  if (locallySignedOut) return <AppPageLoading />;

  return (
    <>
      <div
        className={`app-shell wg-frontend-app${collapsed ? " sidebar-collapsed" : ""}`}
        inert={Boolean(sessionInterrupted)}
        aria-hidden={sessionInterrupted ? true : undefined}
      >
        <aside
          className="sidebar"
          data-wg-shell-sidebar
          onMouseLeave={() => setFlyoutOpen(false)}
        >
          <div className="brand" aria-label={productConfig.brand.accessibleName}>
            <div className="brand-mark" aria-hidden="true">
              <img src={productConfig.brand.logoPath} alt="" />
            </div>
            <div className="brand-name">
              <strong>{productConfig.brand.shortName}</strong>
            </div>
          </div>
          <ControlButton
            className="sidebar-toggle"
            type="button"
            aria-label={collapsed ? "展开侧边栏" : "收起侧边栏"}
            tooltip={collapsed ? "展开侧边栏" : "收起侧边栏"}
            onClick={() => {
              setCollapsed((value) => !value);
              setFlyoutOpen(false);
            }}
          >
            <SvgIcon id="icon-sidebar-toggle" />
          </ControlButton>
          <nav className="nav" aria-label="主导航">
            {configuredMenu
              .filter(
                (entry) =>
                  visibleMenuKeys === null || visibleMenuKeys.has(entry.key),
              )
              .map((entry) => {
                const hasActiveChild =
                  entry.children?.some(
                    (child) => child.key === activeSubMenu,
                  ) ?? false;
                const active =
                  route?.activeMenu === entry.key && !hasActiveChild;
                const isOpen = Boolean(
                  entry.children && (configOpen || hasActiveChild),
                );
                const select = () => {
                  if (entry.children) {
                    if (collapsed) setFlyoutOpen((value) => !value);
                    else setConfigOpen((value) => !value);
                  } else if (entry.path) go(entry.path);
                };
                return (
                  <div
                    className={`nav-group${active ? " active" : ""}${hasActiveChild ? " has-active-child" : ""}${entry.children ? " has-children" : ""}${isOpen ? " is-open" : ""}${flyoutOpen && entry.children ? " is-flyout-open" : ""}`}
                    key={entry.key}
                    onMouseEnter={() => {
                      if (collapsed && entry.children) setFlyoutOpen(true);
                    }}
                  >
                    <div
                      className={`nav-item${active ? " active" : ""}`}
                      tabIndex={0}
                      data-menu-key={entry.key}
                      aria-expanded={
                        entry.children
                          ? collapsed
                            ? flyoutOpen
                            : isOpen
                          : undefined
                      }
                      aria-current={active ? "page" : undefined}
                      onPointerEnter={() => {
                        if (entry.path)
                          void preloadVlaRoute(entry.path).catch(() => undefined);
                      }}
                      onFocus={() => {
                        if (entry.path)
                          void preloadVlaRoute(entry.path).catch(() => undefined);
                      }}
                      onClick={select}
                      onKeyDown={(event) => activateWithKeyboard(event, select)}
                    >
                      <NavigationIcon name={entry.icon} />
                      <span className="nav-label">{entry.label}</span>
                      {entry.children ? (
                        <NavigationIcon name="chevron" className="chevron" />
                      ) : null}
                    </div>
                    {entry.children ? (
                      <div
                        className="nav-submenu"
                        role="group"
                        aria-label={`${entry.label}子菜单`}
                      >
                        {entry.children.map((child) => {
                          const childActive = child.key === activeSubMenu;
                          const selectChild = () => go(child.path);
                          return (
                            <div
                              className={`nav-subitem${childActive ? " active" : ""}`}
                              tabIndex={0}
                              data-menu-key={child.key}
                              aria-current={childActive ? "page" : undefined}
                              key={child.key}
                              onPointerEnter={() =>
                                void preloadVlaRoute(child.path).catch(() => undefined)
                              }
                              onFocus={() =>
                                void preloadVlaRoute(child.path).catch(() => undefined)
                              }
                              onClick={selectChild}
                              onKeyDown={(event) =>
                                activateWithKeyboard(event, selectChild)
                              }
                            >
                              {child.label}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
          </nav>
        </aside>
        <header className="topbar" data-wg-shell-topbar>
          <div className="topbar-left">
            {breadcrumbs.length > 1 && breadcrumbBackPath ? (
              <ControlButton
                className="app-route-back-button"
                type="button"
                aria-label="返回上一页"
                tooltip="返回上一页"
                onClick={() => go(breadcrumbBackPath)}
              >
                <SvgIcon id="icon-chevron" />
              </ControlButton>
            ) : null}
            <Breadcrumbs items={breadcrumbs} navigate={go} />
          </div>
          <div className="top-actions">
            <ControlButton
              className="user-chip without-leading-divider"
              type="button"
              aria-label="打开用户菜单"
              aria-haspopup="menu"
              aria-expanded={Boolean(userMenuAnchor)}
              onMouseEnter={(event) => setUserMenuAnchor(event.currentTarget)}
              onFocus={(event) => setUserMenuAnchor(event.currentTarget)}
              onClick={(event) => setUserMenuAnchor(event.currentTarget)}
            >
              <span>{userName}</span>
            </ControlButton>
          </div>
        </header>
        <main className="main app-main" data-app-main>
          <section className="app-route-stage">
            {navigation.state !== "idle" ? <div className="app-navigation-progress" role="progressbar" aria-label="页面加载中"><span className="page-loading__bar-segment" /></div> : null}
            <div className="app-page-host" data-app-page-host>
              {session && menuScope === authorizationScope && visibleMenuKeys && routeAuthorized && !menuError ? (
                <SessionQueryBoundary key={authorizationScope}>
                  <ErrorBoundary FallbackComponent={AppRouteError} resetKeys={[location.pathname, location.search]}>
                  <Suspense fallback={<AppPageLoading />}>
                    <Outlet context={sessionContext} />
                  </Suspense>
                  </ErrorBoundary>
                </SessionQueryBoundary>
              ) : menuError || (!session && sessionQuery.error && !sessionRejected) ? (
                <AppInitializationFailure error={sessionQuery.error || menuError} pending={sessionQuery.isFetching}
                  retry={() => { if (session) setMenuRetry((value) => value + 1); else void revalidateSession(); }} />
              ) : <AppPageLoading />}
            </div>
          </section>
        </main>
      </div>
      {userMenuAnchor ? (
        <UserMenu
          anchor={userMenuAnchor}
          onClose={() => setUserMenuAnchor(null)}
          onProfile={() => {
            setUserMenuAnchor(null);
            go("/profile-settings");
          }}
          onLogout={() => {
            setUserMenuAnchor(null);
            setLogoutFeedback("");
            setLogoutOpen(true);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={logoutOpen}
        title="退出登录"
        type="warning"
        compact
        preserveTitle
        confirmText="确认退出"
        busy={logoutBusy}
        busyText="退出中"
        onCancel={() => {
          if (!logoutBusy) setLogoutOpen(false);
        }}
        onConfirm={() => void requestProtectedAction(confirmLogout, { navigationTarget: "/login" })}
      />
      <InitialPasswordReminder
        shouldRemind={Boolean(
          session?.account?.shouldRemindInitialPassword,
        )}
        reminderSessionKey={session?.initialPasswordReminderSessionKey}
        onChangePassword={() =>
          navigate("/profile-settings?action=password")
        }
      />
      <NoticeDialog
        open={Boolean(sessionInterrupted)}
        title={
          sessionInterrupted.includes("切换")
            ? "当前浏览器已切换账号"
            : sessionInterrupted.includes("其他设备") ||
                sessionInterrupted.includes("浏览器登录")
              ? "账号已在其他设备登录"
              : sessionInterrupted.includes("过期")
                ? "登录状态已过期"
                : "登录状态已失效"
        }
        content={[
          "当前浏览器已切换登录账号",
          "当前账号已在其他设备或浏览器登录",
          "登录状态已失效，请重新登录",
          "登录状态已过期，请重新登录",
        ].includes(sessionInterrupted) ? "" : sessionInterrupted}
        type="warning"
        buttonText={
          sessionInterrupted.includes("切换")
            ? "刷新为当前账号"
            : "返回登录页"
        }
        onAction={() =>
          sessionInterrupted.includes("切换")
            ? runForcedEditExit(() => window.location.reload())
            : forceEditNavigation("/login", () => { queryClient.clear(); navigate("/login", { replace: true }); })
        }
      />
      <Toast
        message={logoutFeedback}
        durationMs={2600}
        onClose={() => setLogoutFeedback("")}
      />
    </>
  );
}
